import { h, Component } from 'preact';
import { Text } from 'preact-i18n';
import { route } from 'preact-router';

import { attachHls } from '../../lib/hlsAttach';
import { trackVideoPlay } from '../../lib/videoPlayEvent';
import localUtil from '../../lib/util';
import Comments from '../comments';
import ShortsSubtitles from './subtitles';

const SEEN_AFTER_MS = 3000;
// Intersection events fire continuously mid-scroll; calling play() on each one
// produces a pile of aborted play promises and stuttering audio. Only the
// settled clip plays.
const SETTLE_MS = 150;

// Data Saver is an explicit "don't burn my bytes" signal. Autoplaying video over
// it is exactly what the user asked us not to do, so the clip waits for a tap.
function saveDataEnabled() {
    const connection = typeof navigator !== 'undefined'
        ? (navigator.connection || navigator.mozConnection || navigator.webkitConnection)
        : null;
    return !!(connection && connection.saveData);
}

class ShortsClip extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currentTime: 0,
            failed: false,
            blocked: false,
            likeBusy: false,
            showComments: false,
        };
        this.hlsHandle = null;
        this.settleTimer = null;
        this.seenTimer = null;
        // Per-instance dedupe flags (not observable state — nothing renders off
        // these, they just guard one-time side effects for this mounted clip).
        this.playTracked = false;
        this.seenFired = false;
    }

    componentDidMount() {
        this.syncMedia();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.keepAlive !== this.props.keepAlive || prevProps.isActive !== this.props.isActive) {
            this.syncMedia();
        }
        if (prevProps.muted !== this.props.muted && this.videoRef) {
            this.videoRef.muted = this.props.muted;
        }
    }

    componentWillUnmount() {
        this.teardown();
    }

    teardown = () => {
        clearTimeout(this.settleTimer);
        clearTimeout(this.seenTimer);
        // hls.js's destroy() happens to call media.load(), which stops
        // playback as a side effect — but that's an implementation detail of
        // hls.js, not a contract we own (the Safari native-HLS branch of
        // attachHls doesn't even go through hls.js). Stop playback ourselves
        // so teardown doesn't silently depend on it.
        if (this.videoRef) {
            this.videoRef.pause();
        }
        if (this.hlsHandle) {
            this.hlsHandle.destroy();
            this.hlsHandle = null;
        }
    }

    // Attaches hls.js only when nothing is attached yet. Resetting `failed`
    // here (not in teardown) ties the reset to the thing that actually makes
    // the stale error stop being true: a fresh attach.
    ensureHlsAttached = () => {
        const { clip } = this.props;
        if (this.hlsHandle || !this.videoRef || !clip.videoUrl) {
            return;
        }
        this.hlsHandle = attachHls(this.videoRef, clip.videoUrl, {
            onFatal: () => this.setState({ failed: true }),
        });
        if (this.state.failed) {
            this.setState({ failed: false });
        }
    }

    syncMedia = () => {
        const { keepAlive, isActive } = this.props;

        if (!keepAlive) {
            this.teardown();
            return;
        }

        // Data Saver means "don't burn my bytes" — attaching hls.js here would
        // start segment downloads (autoStartLoad defaults true) for every clip
        // sitting in the keep-alive/preload window even though none of them
        // play. Skip the attach entirely; onTap attaches lazily on an explicit
        // user action, which is a deliberate override of the saveData signal.
        if (!saveDataEnabled()) {
            this.ensureHlsAttached();
        }

        clearTimeout(this.settleTimer);
        if (!isActive) {
            if (this.videoRef && !this.videoRef.paused) {
                this.videoRef.pause();
            }
            clearTimeout(this.seenTimer);
            return;
        }

        this.settleTimer = setTimeout(this.playSettled, SETTLE_MS);
    }

    playSettled = () => {
        if (saveDataEnabled()) {
            this.setState({ blocked: true });
            return;
        }

        if (!this.videoRef) {
            return;
        }
        // Safety net: normally already attached by syncMedia's keep-alive pass,
        // but this covers the videoRef-not-ready-yet race and keeps this
        // method self-sufficient.
        this.ensureHlsAttached();
        const playPromise = this.videoRef.play();
        if (playPromise && playPromise.catch) {
            // iOS Low Power Mode refuses even muted autoplay. `blocked` is
            // cleared only by the video's `playing` event (onPlaying below) —
            // never here — so a swipe-away that aborts this same promise
            // (syncMedia's pause() rejects it with AbortError) can set
            // `blocked: true` without leaving it stuck: the NEXT successful
            // play, whenever it happens, is what clears it.
            playPromise.catch(() => this.setState({ blocked: true }));
        }
    }

    scheduleSeen = () => {
        const { clip, onSeen } = this.props;
        clearTimeout(this.seenTimer);
        if (this.seenFired) {
            return;
        }
        this.seenTimer = setTimeout(() => {
            this.seenFired = true;
            onSeen(clip);
        }, SEEN_AFTER_MS);
    }

    // Playback tracking must reflect playback that actually happened: fire
    // trackVideoPlay only from the `playing` event (never optimistically
    // right after calling play()), and only once per mounted instance so
    // bounce-scrolling (swipe away, swipe back re-settling the same clip)
    // can't inflate article.totalVideoPlays.
    onPlaying = () => {
        this.setState({ blocked: false });
        // Scheduling "seen" here (rather than on the autoplay path in
        // playSettled) means it fires for every route into actual playback —
        // autoplay, tap-to-unblock, and retryPlayback alike — including Data
        // Saver users, who only ever reach playback via a tap. scheduleSeen
        // is idempotent (clears/resets its own timer and is a no-op once
        // onSeen has fired), so repeated `playing` events can't double-fire it.
        this.scheduleSeen();
        if (this.playTracked) {
            return;
        }
        this.playTracked = true;
        const { stores, clip } = this.props;
        trackVideoPlay({ stores, articleId: clip.id });
    }

    onTimeUpdate = () => {
        if (this.videoRef) {
            this.setState({ currentTime: this.videoRef.currentTime });
        }
    }

    // Retry affordance for BOTH overlay states: a plain autoplay refusal
    // (blocked) just needs play() called again; a fatal hls.js error
    // (failed) left `this.hlsHandle` pointing at an already-internally-
    // destroyed hls instance, so it must be dropped and re-attached before
    // play() can do anything.
    retryPlayback = () => {
        if (!this.videoRef) {
            return;
        }
        if (this.state.failed && this.hlsHandle) {
            this.hlsHandle.destroy();
            this.hlsHandle = null;
        }
        this.ensureHlsAttached();
        const playPromise = this.videoRef.play();
        if (playPromise && playPromise.catch) {
            playPromise.catch(() => this.setState({ blocked: true }));
        }
    }

    onTap = () => {
        const { onToggleMute } = this.props;
        if (this.state.blocked || this.state.failed) {
            this.retryPlayback();
            return;
        }
        onToggleMute();
    }

    setVideoRef = (c) => {
        this.videoRef = c;
    }

    // Heart tap: like/unlike the clip's article through the same likeStore
    // insert/delete/load calls the shared Likes component (components/likes)
    // uses internally - not a fresh call to fetchApi, and not a re-implementation
    // of Likes' own like/unlike rules. Likes itself isn't rendered here: its
    // showSimpleLike mode has no numeric count (just an icon + "Like" label) and
    // defaults to a thumbs-up icon rather than a heart, while its full mode
    // always renders a ShareButton - which the spec explicitly keeps out of the
    // shorts rail. So the rail keeps its own heart+count markup and drives it
    // through the shared store instead of the shared component's JSX.
    onHeartTap = async (e) => {
        e.stopPropagation();
        const { stores, clip, engagement, onLoadEngagement } = this.props;
        const { userStore, likeStore } = stores;
        const { user, isBlockedFromComments } = userStore;
        if (!user || !user.id) {
            route('/login');
            return;
        }
        // Same gate the shared Likes component (components/likes) applies -
        // a blocked user's like button is never even rendered there, so the
        // heart here is hidden below (render()) for the same flag. This check
        // is the belt-and-suspenders backstop in case onHeartTap is ever
        // reached some other way.
        if (isBlockedFromComments) {
            return;
        }
        if (this.state.likeBusy) {
            return;
        }
        this.setState({ likeBusy: true });
        try {
            const current = engagement || await onLoadEngagement(clip);
            const likes = (current && current.likes) || [];
            const alreadyLiked = likes.some(like => like.user === user.id);
            if (alreadyLiked) {
                await likeStore.delete(1, 'id', { type: 'article', objectId: clip.id });
            } else {
                await likeStore.insert({ type: 'article', objectId: clip.id, likeType: 'like' });
            }
            await onLoadEngagement(clip, { force: true });
        } finally {
            this.setState({ likeBusy: false });
        }
    }

    // Comment tap: opens the actual Comments component (components/comments) in
    // a bottom sheet scoped to this clip - never a modal, and never the app-wide
    // appState.openDrawer stack (no existing drawer key renders comments alone;
    // the only candidate, 'viewArticle', pulls in the whole article - heading,
    // body, images, related posts, its own share row - which is the wrong shape
    // for a quick comment check mid-clip).
    onCommentTap = (e) => {
        e.stopPropagation();
        const { stores, clip, onLoadEngagement } = this.props;
        const { user } = stores.userStore;
        if (!user || !user.id) {
            route('/login');
            return;
        }
        onLoadEngagement(clip);
        this.setState({ showComments: true });
    }

    closeComments = (e) => {
        if (e) {
            e.stopPropagation();
        }
        this.setState({ showComments: false });
    }

    onStatsTap = (e) => {
        e.stopPropagation();
        const { stores, clip } = this.props;
        const { appState } = stores;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('articleStats', {
            height: drawerHeightLarge,
            articleId: clip.id,
        }, 1);
    }

    render() {
        const {
            clip, index, muted, subtitles = [], engagement, stores,
        } = this.props;
        const {
            currentTime, failed, blocked, likeBusy, showComments,
        } = this.state;
        const { userStore } = stores;
        const { user, isBlockedFromComments, isAdmin } = userStore;

        // engagement is undefined until onLoadEngagement resolves (or forever, for
        // an anonymous viewer - /api/likes and /api/comments both require a
        // logged-in user). Until then, fall back to the aggregate counts
        // get-shorts.js already returned for every clip in the page.
        const likes = engagement?.likes;
        const comments = engagement?.comments;
        const likeCount = Array.isArray(likes) ? likes.length : clip.likeCount;
        const commentCount = Array.isArray(comments) ? comments.length : clip.commentCount;
        const hasLiked = Array.isArray(likes) && !!user?.id && likes.some(like => like.user === user.id);

        return (
            <div
                class='position-relative w-100 bg-black'
                // height: 100% (not 100dvh) - this clip must match whatever height the
                // ShortsFeed scroll container actually has, which ShortsFeed itself
                // already sizes to the visible area beneath the app's fixed header +
                // feed tab bar (see feed.js). Using 100dvh here would re-introduce the
                // exact overflow bug this pairs with: a clip taller than the space the
                // shell actually gives it, pushing the subtitle/caption/action-rail
                // below the fold.
                style='height: 100%; scroll-snap-align: start; scroll-snap-stop: always; overflow: hidden;'
                data-index={index}
                data-uuidv4={clip.uuidv4}
            >
                <video
                    ref={this.setVideoRef}
                    class='w-100 h-100'
                    style='object-fit: contain;'
                    poster={clip.poster}
                    muted={muted}
                    loop
                    playsinline
                    webkit-playsinline='true'
                    preload='metadata'
                    onTimeUpdate={this.onTimeUpdate}
                    onPlaying={this.onPlaying}
                    onClick={this.onTap}
                />

                <ShortsSubtitles segments={subtitles} currentTime={currentTime} />

                {(blocked || failed) && (
                    <div
                        class='position-absolute top-50 start-50 translate-middle d-flex flex-column align-items-center text-white'
                        style='pointer-events: none;'
                    >
                        <span
                            class='d-flex align-items-center justify-content-center rounded-circle'
                            style='width: 64px; height: 64px; background: rgba(0,0,0,0.45); border: 2px solid rgba(255,255,255,0.85);'
                        >
                            <i class={`fa-solid ${failed ? 'fa-rotate-right' : 'fa-play'}`} style='font-size: 1.4rem; margin-left: 3px;' />
                        </span>
                        {failed && (
                            <small class='mt-2'>
                                <Text id='shorts.playback-failed'>Could not play this video</Text>
                            </small>
                        )}
                    </div>
                )}

                {/* The shorts wrapper is fullscreen (routes/frontpage/loggedIn.js and
                    notLoggedIn.js) and the global fixed-bottom nav is hidden, so the
                    clip reaches the physical screen edge - the safe-area inset keeps
                    the caption (and the action rail below, same offset) clear of the
                    iPhone home indicator. */}
                <div class='position-absolute w-100 px-3' style='bottom: calc(24px + env(safe-area-inset-bottom, 0px)); left: 0; pointer-events: none;'>
                    <div class='text-white fw-bold' style='font-size: 0.95rem;'>
                        {clip.creator}
                    </div>
                    <div
                        class='text-white-50'
                        style='font-size: 0.85rem; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;'
                    >
                        {clip.title}
                    </div>
                </div>

                <div
                    class='position-absolute d-flex flex-column align-items-center text-white'
                    style='right: 12px; bottom: calc(24px + env(safe-area-inset-bottom, 0px)); gap: 18px; pointer-events: none;'
                >
                    {/* Same gate as the shared Likes component (components/likes ~line 355):
                        a blocked user's like control is never rendered there, so the heart
                        is hidden here too rather than left tappable-but-inert. */}
                    {!isBlockedFromComments && (
                        <span
                            class='d-flex flex-column align-items-center'
                            style='pointer-events: auto; cursor: pointer;'
                            onClick={this.onHeartTap}
                        >
                            <i
                                class={`fa-${hasLiked ? 'solid' : 'regular'} fa-heart ${likeBusy ? 'opacity-50' : ''}`}
                                style={`font-size: 1.5rem; ${hasLiked ? 'color: #e74c3c;' : ''}`}
                            />
                            {likeCount > 0 && <small>{likeCount}</small>}
                        </span>
                    )}
                    <span
                        class='d-flex flex-column align-items-center'
                        style='pointer-events: auto; cursor: pointer;'
                        onClick={this.onCommentTap}
                    >
                        <i class={`fa-${showComments ? 'solid' : 'regular'} fa-comment`} style='font-size: 1.5rem;' />
                        {commentCount > 0 && <small>{commentCount}</small>}
                    </span>
                    <span class='d-flex flex-column align-items-center'>
                        <i class={`fa-solid ${muted ? 'fa-volume-xmark' : 'fa-volume-high'}`} style='font-size: 1.3rem;' />
                    </span>
                    {(isAdmin || (clip.user > 0 && clip.user === user?.id)) && (
                        <span
                            class='d-flex flex-column align-items-center'
                            style='pointer-events: auto; cursor: pointer;'
                            onClick={this.onStatsTap}
                            title='Show detailed statistics'
                        >
                            <i class='fa-solid fa-chart-simple' style='font-size: 1.3rem;' />
                        </span>
                    )}
                </div>

                {showComments && (
                    <>
                        {/* Scrim: scoped to this clip only (not a fixed/app-wide overlay), so
                            it never registers as a modal - it just dims the clip behind the
                            sheet and lets tapping outside the sheet close it. touch-action:
                            none stops a swipe-to-dismiss gesture here from also being read as
                            a swipe to the next clip by the feed's scroll-snap container. */}
                        <div
                            class='position-absolute top-0 start-0 w-100 h-100'
                            style='background: rgba(0,0,0,0.35); z-index: 20; pointer-events: auto; touch-action: none;'
                            onClick={this.closeComments}
                            onTouchMove={localUtil.captureEvents}
                        />
                        <div
                            class='position-absolute bottom-0 start-0 w-100 bg-body d-flex flex-column'
                            style='height: 68%; z-index: 21; pointer-events: auto; border-radius: 10px 10px 0 0; overflow: hidden;'
                            onClick={e => e.stopPropagation()}
                            onTouchMove={localUtil.captureEvents}
                            onWheel={localUtil.captureEvents}
                        >
                            <div class='d-flex align-items-center justify-content-between px-3 py-2 border-bottom flex-shrink-0'>
                                <span class='fw-bold text-body'>
                                    <Text id='shorts.comments-title'>Comments</Text>
                                </span>
                                <button type='button' class='btn btn-sm btn-link text-body p-0' onClick={this.closeComments}>
                                    <i class='fa-solid fa-xmark' style='font-size: 1.2rem;' />
                                </button>
                            </div>
                            <div
                                class='flex-grow-1 overflow-auto'
                                style='overscroll-behavior: contain;'
                                onScroll={localUtil.captureEvents}
                            >
                                <Comments
                                    stores={stores}
                                    type='article'
                                    objectId={clip.id}
                                    comments={Array.isArray(comments) ? comments : []}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }
}

ShortsClip.defaultProps = {
    engagement: undefined,
    onLoadEngagement: () => Promise.resolve(null),
    onSeen: () => {},
    onToggleMute: () => {},
};

export default ShortsClip;
